import React, { useState, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Progress,
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col
} from 'reactstrap';
import * as XLSX from 'xlsx';
import Dropzone from 'react-dropzone';
import { FiFile, FiFileText, FiImage, FiDownload, FiEye, FiTrash2, FiArrowUp, FiList, FiUploadCloud } from 'react-icons/fi';
import { filesize } from 'filesize';
import PropTypes from 'prop-types';
import './FileManager.css'; // CSS dosyası oluşturacağız

const SUPPORTED_FORMATS = {
  images: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  documents: ['.pdf', '.doc', '.docx', '.xls', '.xlsx'],
  others: ['.zip', '.rar', '.txt']
};

const FileManager = ({
  title,
  files = [],
  field,
  isEditing,
  pb,
  recordId,
  onUpload,
  onDelete,
  dropzoneText = "Dosyaları buraya sürükleyin veya yüklemek için tıklayın",
  allowedFileTypes = [...SUPPORTED_FORMATS.images, ...SUPPORTED_FORMATS.documents, ...SUPPORTED_FORMATS.others],
  maxFileSize = 10 * 1024 * 1024,
  multiple = true
}) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [previewFile, setPreviewFile] = useState(null);
  const [tempFiles, setTempFiles] = useState([]); // Geçici dosyaları tutacak state
  const [excelData, setExcelData] = useState(null);

  // Dosya tipi kontrolü
  const getFileInfo = (filename) => {
    if (!filename) return { type: 'other', icon: <FiFile className="text-muted" size={20} /> };

    const extension = '.' + filename.split('.').pop().toLowerCase();
    let type = 'other';
    let icon = <FiFile className="text-muted" size={20} />;

    if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(extension)) {
      type = 'image';
      icon = <FiImage className="text-primary" size={20} />;
    } else if (['.pdf'].includes(extension)) {
      type = 'pdf';
      icon = <FiFileText className="text-danger" size={20} />;
    } else if (['.xlsx', '.xls'].includes(extension)) {
      type = 'excel';
      icon = <FiFileText className="text-success" size={20} />;
    } else if (['.doc', '.docx'].includes(extension)) {
      type = 'document';
      icon = <FiFileText className="text-info" size={20} />;
    }

    return { type, icon, extension };
  };

  // Dosya URL'i oluşturma
  const getFileUrl = useCallback((filename) => {
    if (!filename || !recordId) {
      console.log('Missing filename or recordId:', { filename, recordId });
      return null;
    }

    try {
      // PocketBase'in API URL'ini ve tam dosya yolunu logla
      const record = {
        collectionId: 'customers',
        id: recordId,
        filename: filename
      };

      const fileUrl = pb.files.getUrl(record, filename);
      console.log('Generated File URL:', {
        filename,
        recordId,
        url: fileUrl,
        baseUrl: pb.baseUrl,
        collection: 'customers'
      });

      return fileUrl;
    } catch (error) {
      console.error('File URL generation error:', error, {
        filename,
        recordId,
        pbBaseUrl: pb.baseUrl
      });
      return null;
    }
  }, [pb, recordId]);

  const handleExcelPreview = async (url) => {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);
      const workbook = XLSX.read(data, {
        type: 'array',
        cellDates: true,
        cellStyles: true
      });
      
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      setExcelData(jsonData);
    } catch (error) {
      console.error('Excel preview error:', error);
      setError('Failed to load Excel preview');
    }
  };

  // Dosya yükleme
  const handleDrop = useCallback(async (acceptedFiles) => {
    setError(null);

    try {
      // Dosyaları geçici state'e ekle
      const newTempFiles = acceptedFiles.map(file => ({
        file,
        id: Math.random().toString(36).substring(7),
        preview: URL.createObjectURL(file)
      }));

      setTempFiles(prev => [...prev, ...newTempFiles]);

    } catch (error) {
      console.error('Upload error:', error);
      setError(`Dosya önizleme hatası: ${error.message}`);
    }
  }, []);

  // Geçici dosyayı kaldır
  const removeTempFile = useCallback((fileId) => {
    setTempFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  // Tüm dosyaları yükle
  const uploadAllFiles = useCallback(async () => {
    setError(null);
    const formData = new FormData();

    // Use the field name with '+' suffix for multiple file uploads
    tempFiles.forEach(({ file }) => {
      formData.append(`${field}+`, file);
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
    });

    try {
      const result = await pb.collection('customers').update(recordId, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          tempFiles.forEach(({ file }) => {
            setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
          });
        }
      });

      if (result) {
        tempFiles.forEach(({ file }) => {
          setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        });
        onUpload(result, field);
        setTempFiles([]); // Clear temporary files
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(`File upload error: ${error.message}`);
      tempFiles.forEach(({ file }) => {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
      });
    }
  }, [tempFiles, pb, recordId, onUpload, field]);

  // Önizleme modal'ı
  const renderPreviewModal = () => {
    if (!previewFile) return null;

    const url = getFileUrl(previewFile.name);
    const { type } = getFileInfo(previewFile.name);

    if (!url) {
      return (
        <Modal isOpen={!!previewFile} toggle={() => setPreviewFile(null)} size="lg">
          <ModalHeader toggle={() => setPreviewFile(null)}>Error</ModalHeader>
          <ModalBody>
            <div className="text-center p-5">
              <FiFile size={48} className="text-danger" />
              <p className="mt-3">Failed to generate file preview URL</p>
            </div>
          </ModalBody>
        </Modal>
      );
    }

    return (
        <Modal isOpen={!!previewFile} toggle={() => setPreviewFile(null)} size="lg" className="file-preview-modal">
          <ModalHeader toggle={() => setPreviewFile(null)}>{previewFile.name}</ModalHeader>
          <ModalBody>
            {type === 'image' && (
              <img src={url} alt={previewFile.name} className="img-fluid" />
            )}
            
            {type === 'pdf' && (
              <div className="pdf-container">
                <embed
                  src={url}
                  type="application/pdf"
                  width="100%"
                  height="600px"
                />
              </div>
            )}
            
            {type === 'excel' && (
              <div className="excel-container">
                {excelData ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                      <thead>
                        <tr>
                          {excelData[0]?.map((header, index) => (
                            <th key={index}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {excelData.slice(1).map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center p-5">Loading Excel data...</div>
                )}
              </div>
            )}
            
            {!['image', 'pdf', 'excel'].includes(type) && (
              <div className="text-center p-5">
                <FiFile size={48} />
                <p className="mt-3">Preview not available for this file type</p>
                <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-primary mt-2">
                  Open File
                </a>
              </div>
            )}
          </ModalBody>
        </Modal>
      );
    };

  // Dosya listesi
  const renderFiles = () => {
    if (!files?.length) {
      return (
        <div className="text-center text-muted py-4">
          <p className="mb-0">Henüz dosya yüklenmemiş</p>
        </div>
      );
    }

    return (
      <Row className="g-3 file-grid">
        {files.map((file, index) => {
          const { icon, type } = getFileInfo(file);
          const progress = uploadProgress[file] || 0;
          const url = getFileUrl(file);

          return (
            <Col key={index} xs={12} sm={6} md={4} lg={3}>
              <div className="file-card">
                <div className="file-card-preview" onClick={() => setPreviewFile({ name: file, type })}>
                  {type === 'image' ? (
                    <img src={url} alt={file} className="file-thumbnail" />
                  ) : (
                    <div className="file-icon">{icon}</div>
                  )}
                </div>
                <div className="file-card-info">
                  <h6 className="file-name">{file}</h6>
                  <div className="file-actions">
                    <button className="btn btn-link btn-sm" onClick={() => setPreviewFile({ name: file, type })}>
                      <FiEye />
                    </button>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-link btn-sm">
                      <FiDownload />
                    </a>
                    {isEditing && (
                      <button className="btn btn-link btn-sm text-danger" onClick={() => onDelete(field, file)}>
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                </div>
                {progress > 0 && progress < 100 && (
                  <Progress value={progress} className="file-progress" />
                )}
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  const handlePreview = async (file) => {
    const { type } = getFileInfo(file.name);
    setPreviewFile(file);
    
    if (type === 'excel') {
      const url = getFileUrl(file.name);
      await handleExcelPreview(url);
    }
  };

  return (
    <Card className="file-manager">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">{title}</h5>
        {tempFiles.length > 0 && (
          <div className="d-flex gap-2">
            <Button color="success" size="sm" onClick={uploadAllFiles}>
              <i className="ri-upload-2-line align-bottom me-1"></i>
              Yükle ({tempFiles.length})
            </Button>
            <Button color="danger" size="sm" onClick={() => setTempFiles([])}>
              <i className="ri-close-line align-bottom me-1"></i>
              İptal
            </Button>
          </div>
        )}
      </CardHeader>

      <CardBody>
        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isEditing && (
          <Dropzone
            onDrop={handleDrop}
            accept={{
              'image/*': SUPPORTED_FORMATS.images,
              'application/pdf': ['.pdf'],
              'application/msword': ['.doc', '.docx'],
              'application/vnd.ms-excel': ['.xls', '.xlsx'],
              'application/zip': ['.zip'],
              'application/x-rar-compressed': ['.rar'],
              'text/plain': ['.txt']
            }}
            multiple={multiple}
            maxSize={maxFileSize}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div {...getRootProps()} className={`dropzone ${isDragActive ? 'dropzone-active' : ''}`}>
                <input {...getInputProps()} />
                <div className="dropzone-content">
                  <FiUploadCloud size={48} className="dropzone-icon" />
                  <h4>{dropzoneText}</h4>
                  <p className="text-muted">
                    {`İzin verilen dosya tipleri: ${allowedFileTypes.join(', ')}`}<br />
                    {`Maksimum dosya boyutu: ${filesize(maxFileSize)}`}
                  </p>
                </div>
              </div>
            )}
          </Dropzone>
        )}

        {/* Geçici dosyaların listesi */}
        {tempFiles.length > 0 && (
          <div className="mt-4">
            <h6 className="mb-3">Yüklenecek Dosyalar</h6>
            <Row className="g-3">
              {tempFiles.map(({ file, id, preview }) => (
                <Col key={id} xs={12} sm={6} md={4} lg={3}>
                  <div className="file-card">
                    <div className="file-card-preview">
                      {file.type.startsWith('image/') ? (
                        <img src={preview} alt={file.name} className="file-thumbnail" />
                      ) : (
                        <div className="file-icon">
                          <FiFile size={32} />
                        </div>
                      )}
                    </div>
                    <div className="file-card-info">
                      <h6 className="file-name">{file.name}</h6>
                      <small className="text-muted d-block">
                        {filesize(file.size)}
                      </small>
                      <Button
                        color="link"
                        className="p-0 text-danger"
                        onClick={() => removeTempFile(id)}
                      >
                        <FiTrash2 />
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Mevcut dosyaların listesi */}
        <div className="mt-4">
          {files.length > 0 && <h6 className="mb-3">Yüklenen Dosyalar</h6>}
          {renderFiles()}
        </div>
        {renderPreviewModal()}
      </CardBody>
    </Card>
  );
};

FileManager.propTypes = {
  title: PropTypes.string.isRequired,
  files: PropTypes.array,
  field: PropTypes.string.isRequired,
  isEditing: PropTypes.bool,
  pb: PropTypes.object.isRequired,
  recordId: PropTypes.string.isRequired,
  onUpload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  dropzoneText: PropTypes.string,
  allowedFileTypes: PropTypes.array,
  maxFileSize: PropTypes.number,
  multiple: PropTypes.bool
};

export default FileManager;